/**
 * contextApi.ts
 *
 * Typed API client functions for context execution and cache management.
 * These wrap the generic ApiClient with context-specific URLs and types.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { ContextExecutionResult } from '../hooks/useContextData';
import type { Context, LocalizedString, ContextApplicability, ContextDefinition, EnrichmentOverrides } from 'types/context';
import type { BaseViewConfig, BaseViewConfigPatch, TreeMapViewConfig } from 'types/contextView';

/** Context view record from schema-service */
export interface ContextViewRecord {
  id: string;
  system_name: string;
  name: { default: string; translations?: Record<string, string> };
  description?: { default: string; translations?: Record<string, string> };
  organization_id: string;
  context_ref: string;
  applicability?: ContextApplicability;
  view_type: 'atlas' | 'treemap' | 'kanban' | 'table' | 'timeline' | 'custom' | 'scorecard';
  base_config: BaseViewConfig;
  view_config: Record<string, unknown>;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  /** Whether this view is visible to the organization beyond its creator. Private (`false`) by default. */
  listed: boolean;
  /** ISO 8601 timestamp of when this view was last listed. `null` iff `listed` is `false`. */
  listed_at: string | null;
}

/** Parameters for executing a context */
export interface ExecuteContextParams {
  rootSphereId: string;
  limit?: number;
  offset?: number;
  depth?: number;
  skipCache?: boolean;
  refreshStructure?: boolean;
  refreshData?: boolean;
  enrichmentOverrides?: EnrichmentOverrides;
}

/**
 * Execute a context query against graph-service.
 *
 * @param graphClient - Graph service API client
 * @param systemName - Context system name
 * @param params - Execution parameters
 */
export async function executeContext(
  graphClient: ApiClient,
  systemName: string,
  params: ExecuteContextParams
): Promise<ContextExecutionResult> {
  const { rootSphereId, limit, offset, depth, skipCache, refreshStructure, refreshData, enrichmentOverrides } = params;

  const queryParams = new URLSearchParams();
  queryParams.set('rootSphereId', rootSphereId);
  if (limit !== undefined) queryParams.set('limit', String(limit));
  if (offset !== undefined) queryParams.set('offset', String(offset));
  if (depth !== undefined) queryParams.set('depth', String(depth));
  if (skipCache) queryParams.set('skipCache', 'true');
  if (refreshStructure) queryParams.set('refreshStructure', 'true');
  if (refreshData) queryParams.set('refreshData', 'true');
  if (enrichmentOverrides?.includeStatus !== undefined) queryParams.set('includeStatus', String(enrichmentOverrides.includeStatus));
  if (enrichmentOverrides?.includeFlow !== undefined) queryParams.set('includeFlow', String(enrichmentOverrides.includeFlow));
  if (enrichmentOverrides?.includeEdgeCount !== undefined) queryParams.set('includeEdgeCount', String(enrichmentOverrides.includeEdgeCount));
  if (enrichmentOverrides?.includePerspectives !== undefined) queryParams.set('includePerspectives', String(enrichmentOverrides.includePerspectives));
  if (enrichmentOverrides?.includeApproachDeployments !== undefined) queryParams.set('includeApproachDeployments', String(enrichmentOverrides.includeApproachDeployments));
  if (enrichmentOverrides?.includePoints !== undefined) queryParams.set('includePoints', String(enrichmentOverrides.includePoints));

  const url = `/contexts/${encodeURIComponent(systemName)}/execute?${queryParams.toString()}`;
  return graphClient.get<ContextExecutionResult>(url);
}

/**
 * Invalidate the structure cache for a context.
 *
 * @param graphClient - Graph service API client
 * @param systemName - Context system name
 */
export async function invalidateContextCache(
  graphClient: ApiClient,
  systemName: string
): Promise<void> {
  await graphClient.delete(`/contexts/${encodeURIComponent(systemName)}/cache`);
}

/**
 * Invalidate all caches (structure + data) for a specific sphere.
 *
 * @param graphClient - Graph service API client
 * @param sphereId - Sphere ID
 */
export async function invalidateSphereCaches(
  graphClient: ApiClient,
  sphereId: string
): Promise<void> {
  await graphClient.delete(`/spheres/${encodeURIComponent(sphereId)}/cache`);
}

/**
 * Fetch a context view by system name from schema-service.
 *
 * @param schemaClient - Schema service API client
 * @param systemName - Context view system name
 */
export async function getContextView(
  schemaClient: ApiClient,
  systemName: string
): Promise<ContextViewRecord> {
  return schemaClient.get<ContextViewRecord>(
    `/context-views/system_name/${encodeURIComponent(systemName)}`
  );
}

/**
 * Fetch all context views for a given context ref from schema-service.
 *
 * @param schemaClient - Schema service API client
 * @param contextRef - Context system name
 */
export async function getContextViewsByContext(
  schemaClient: ApiClient,
  contextRef: string
): Promise<ContextViewRecord[]> {
  try {
    return await schemaClient.get<ContextViewRecord[]>(
      `/context-views/by-context/${encodeURIComponent(contextRef)}`
    );
  } catch (err) {
    if ((err as { response?: { status?: number } })?.response?.status === 404) return [];
    throw err;
  }
}

// ============================================================================
// Context CRUD (schema-service)
// ============================================================================

/** Parameters for creating a context (systemName is auto-generated from name) */
export interface CreateContextPayload {
  name: LocalizedString;
  description?: LocalizedString;
  applicability?: ContextApplicability;
  definition: ContextDefinition;
  useCaseTags?: string[];
}

/** Parameters for updating a context */
export interface UpdateContextPayload {
  name?: LocalizedString;
  description?: LocalizedString;
  applicability?: ContextApplicability;
  definition?: ContextDefinition;
  useCaseTags?: string[];
}

/** Parameters for cloning a context (system name is auto-generated from newName) */
export interface CloneContextPayload {
  newName: LocalizedString;
  /** When true, clone associated context views along with the context */
  includeViews?: boolean;
  /** Override the cloned context's definition (applied after copy) */
  definitionOverride?: Partial<ContextDefinition>;
  /** Per-view config overrides applied to cloned views */
  viewOverrides?: Array<{
    systemName: string;
    viewConfig?: Partial<Record<string, unknown>>;
    baseConfig?: Partial<BaseViewConfig>;
  }>;
}

/** Response from clone endpoint */
export interface CloneContextResponse {
  context: Context;
  clonedViews: ContextViewRecord[];
}

/**
 * Normalize a context's JSONB-backed `use_case_tags` to a clean string array.
 * Some rows (notably cloned contexts) can come back with a non-array value
 * because a raw JS array inserted into a `jsonb` column is serialized by the
 * pg driver as a Postgres array literal. Coerce defensively so consumers
 * (filters, the use-case MultiSelect) never receive a non-array.
 */
function normalizeContext(context: Context): Context {
  const raw = (context as { use_case_tags?: unknown }).use_case_tags;
  if (Array.isArray(raw)) return context;

  let tags: string[] = [];
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed: unknown = JSON.parse(raw);
      tags = Array.isArray(parsed)
        ? parsed.filter((v): v is string => typeof v === 'string')
        : raw.split(',').map((s) => s.trim()).filter(Boolean);
    } catch {
      tags = raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return { ...context, use_case_tags: tags };
}

/**
 * List all contexts for the caller's organization (scoped server-side from the token).
 */
export async function listContexts(schemaClient: ApiClient): Promise<Context[]> {
  const contexts = await schemaClient.get<Context[]>('/contexts/list');
  return Array.isArray(contexts) ? contexts.map(normalizeContext) : [];
}

/**
 * Get a single context by system name.
 */
export async function getContext(
  schemaClient: ApiClient,
  systemName: string
): Promise<Context> {
  const context = await schemaClient.get<Context>(
    `/contexts/system_name/${encodeURIComponent(systemName)}`
  );
  return normalizeContext(context);
}

/**
 * Create a new context.
 */
export async function createContext(
  schemaClient: ApiClient,
  payload: CreateContextPayload
): Promise<{ context: Context }> {
  return schemaClient.post<{ context: Context }>('/contexts', payload);
}

/**
 * Update an existing context.
 */
export async function updateContext(
  schemaClient: ApiClient,
  systemName: string,
  payload: UpdateContextPayload
): Promise<{ context: Context }> {
  return schemaClient.put<{ context: Context }>(
    `/contexts/system_name/${encodeURIComponent(systemName)}`,
    payload
  );
}

/**
 * Delete a context by system name.
 */
export async function deleteContext(
  schemaClient: ApiClient,
  systemName: string
): Promise<{ success: boolean; message: string }> {
  return schemaClient.delete<{ success: boolean; message: string }>(
    `/contexts/system_name/${encodeURIComponent(systemName)}`
  );
}

/**
 * Clone a context and its associated views.
 */
export async function cloneContext(
  schemaClient: ApiClient,
  systemName: string,
  payload: CloneContextPayload
): Promise<CloneContextResponse> {
  return schemaClient.post<CloneContextResponse>(
    `/contexts/system_name/${encodeURIComponent(systemName)}/clone`,
    payload
  );
}

// ============================================================================
// Context View CRUD (schema-service)
// ============================================================================

/** Payload for creating a new context view */
export interface CreateContextViewPayload {
  systemName: string;
  name: LocalizedString;
  description?: LocalizedString;
  applicability?: ContextApplicability;
  contextRef: string;
  viewType: 'atlas' | 'treemap' | 'kanban' | 'table' | 'timeline' | 'custom' | 'scorecard';
  baseConfig: BaseViewConfig | Record<string, unknown>;
  viewConfig: TreeMapViewConfig | Record<string, unknown>;
}

/** Payload for updating an existing context view */
export interface UpdateContextViewPayload {
  name?: LocalizedString;
  description?: LocalizedString;
  applicability?: ContextApplicability;
  contextRef?: string;
  viewType?: string;
  baseConfig?: Partial<BaseViewConfig> | Record<string, unknown>;
  viewConfig?: Partial<TreeMapViewConfig> | Record<string, unknown>;
}

/** Per-context permission decisions for the requesting user */
export interface ContextPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
}

/** Per-context-view permission decisions for the requesting user */
export interface ContextViewPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canPublish: boolean;
}

/**
 * Create a new context view.
 */
export async function createContextView(
  schemaClient: ApiClient,
  payload: CreateContextViewPayload
): Promise<ContextViewRecord> {
  const response = await schemaClient.post<{ contextView: ContextViewRecord }>(
    '/context-views',
    payload
  );
  return response.contextView;
}

/**
 * Update an existing context view by system name.
 */
export async function updateContextView(
  schemaClient: ApiClient,
  systemName: string,
  payload: UpdateContextViewPayload
): Promise<ContextViewRecord> {
  const response = await schemaClient.put<{ contextView: ContextViewRecord }>(
    `/context-views/system_name/${encodeURIComponent(systemName)}`,
    payload
  );
  return response.contextView;
}

/**
 * Delete a context view by system name.
 */
export async function deleteContextView(
  schemaClient: ApiClient,
  systemName: string
): Promise<{ success: boolean; message: string }> {
  return schemaClient.delete<{ success: boolean; message: string }>(
    `/context-views/system_name/${encodeURIComponent(systemName)}`
  );
}

/**
 * Get permission decisions for the current user on a context.
 *
 * Returns { canEdit, canDelete, canShare } from the server-side SpiceDB check.
 * Route added in Phase 1 of treemap-context-switcher. Fail-closed: if this
 * call throws, callers should treat all permissions as false.
 */
export async function getContextPermissions(
  schemaClient: ApiClient,
  systemName: string
): Promise<ContextPermissions> {
  return schemaClient.get<ContextPermissions>(
    `/contexts/system_name/${encodeURIComponent(systemName)}/permissions`
  );
}

/**
 * Get permission decisions for the current user on a context view.
 *
 * Returns { canEdit, canDelete, canPublish } from the server-side SpiceDB
 * check against the view's own `context_view` resource. Fail-closed: if this
 * call throws, callers should treat all permissions as false.
 */
export async function getContextViewPermissions(
  schemaClient: ApiClient,
  systemName: string
): Promise<ContextViewPermissions> {
  return schemaClient.get<ContextViewPermissions>(
    `/context-views/system_name/${encodeURIComponent(systemName)}/permissions`
  );
}

/**
 * List or unlist a context view, controlling its visibility to the rest of
 * the organization. Requires `publish` on the view (context edit) —
 * a 403 means the caller lacks that right.
 */
export async function setContextViewListing(
  schemaClient: ApiClient,
  systemName: string,
  listed: boolean
): Promise<{ listed: boolean }> {
  return schemaClient.post<{ listed: boolean }>(
    `/context-views/system_name/${encodeURIComponent(systemName)}/listing`,
    { listed }
  );
}

// ============================================================================
// Context view personalization overlay (schema-service)
// ============================================================================

/** The caller's own personalization overlay, or `null` when none exists (204). */
export interface ContextViewStateOverlay {
  baseConfig: Partial<BaseViewConfig>;
  viewConfig: Partial<Record<string, unknown>>;
  updatedAt: string;
}

/**
 * Partial patch merged per-key with whatever is already stored server-side.
 * `baseConfig` admits `null` (a `BaseViewConfigPatch`) at the top level or
 * per-entry inside `levelFilters`, deleting rather than replacing — the
 * server applies the same RFC 7386 `jsonb_deep_merge` semantics
 * `deepMergeJson` mirrors client-side.
 */
export interface UpsertContextViewStatePayload {
  viewSystemName?: string;
  baseConfig?: BaseViewConfigPatch;
  viewConfig?: Partial<Record<string, unknown>>;
}

/**
 * Read the caller's own personalization overlay for a context. `viewSystemName`
 * omitted addresses the implicit default view (no saved ContextView).
 * Returns `null` on 204 — "no overlay" is a normal, expected state, not an error.
 */
export async function getContextViewState(
  schemaClient: ApiClient,
  contextRef: string,
  viewSystemName?: string
): Promise<ContextViewStateOverlay | null> {
  const query = viewSystemName ? `?viewSystemName=${encodeURIComponent(viewSystemName)}` : '';
  // The client extracts a 204 (no overlay) as `undefined` rather than throwing
  // — see ApiClients.ts's `extract`. Normalize that to `null` here.
  const overlay = await schemaClient.get<ContextViewStateOverlay | undefined>(
    `/context-view-state/${encodeURIComponent(contextRef)}${query}`
  );
  return overlay ?? null;
}

/**
 * Upsert the caller's own personalization overlay. The payload is a PARTIAL —
 * the server merges it per key with what is already stored, so omitted keys
 * survive rather than being cleared.
 */
export async function putContextViewState(
  schemaClient: ApiClient,
  contextRef: string,
  payload: UpsertContextViewStatePayload
): Promise<ContextViewStateOverlay> {
  return schemaClient.put<ContextViewStateOverlay>(
    `/context-view-state/${encodeURIComponent(contextRef)}`,
    payload
  );
}

/**
 * Reset: delete the caller's own overlay row. Idempotent — resolves whether
 * or not a row existed.
 */
export async function deleteContextViewState(
  schemaClient: ApiClient,
  contextRef: string,
  viewSystemName?: string
): Promise<void> {
  const query = viewSystemName ? `?viewSystemName=${encodeURIComponent(viewSystemName)}` : '';
  await schemaClient.delete<void>(
    `/context-view-state/${encodeURIComponent(contextRef)}${query}`
  );
}
